import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpEventType, HttpResponse, HttpHeaders } from '@angular/common/http';
import { AppConfig } from '../app.config';
import { combineLatest, Observable, of } from 'rxjs';
import { ReportConverter } from './converters/ReportConverter';
import { catchError, map, tap, mergeMap } from 'rxjs/operators';
import { Route } from '../models/segment';
import { CustomHttpParamsEncoder } from './settings/CustomHttpParamsEncoder';
import { Accessibility, MapType } from '../components/management/manage-map/registry';
import { LayerConverter } from './converters/LayerConverter';
import { EntityConverter } from "./converters/EntitiConverter";
import _ from "lodash";
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  checksum: any = {};

  constructor(
    private http: HttpClient,
    private config: AppConfig,
    private translate: TranslateService
  ) {
  }

  get<T>(endpoint: string, ...args): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept-Language', this.translate.getBrowserLang());
    return this.http.get<T>(endpoint, { ...args[0], headers });
  }

  post<T>(endpoint: string, body: any, ...args): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept-Language', this.translate.getBrowserLang());
    return this.http.post<T>(endpoint, body, { ...args[0], headers });
  }

  put<T>(endpoint: string, body: any, ...args): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept-Language', this.translate.getBrowserLang());
    return this.http.put<T>(endpoint, body, { ...args[0], headers });
  }

  delete<T>(endpoint: string, ...args): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept-Language', this.translate.getBrowserLang());
    return this.http.delete<T>(endpoint, { ...args[0], headers });
  }

  genRouteCreating(projectId: number): Observable<boolean> {
    let params = this.createHttpParams().set('project_id', projectId.toString());

    return this.get<boolean>(this.config.getEndpoint('gen_route/creating'), { params: params });
  }

  genRouteCreated(projectId: number): Observable<boolean> {
    let params = this.createHttpParams().set('project_id', projectId.toString());

    return this.get<boolean>(this.config.getEndpoint('gen_route/created'), { params: params });
  }

  genRouteInProject(projectId: number): Observable<any> {
    let params = this.createHttpParams().set('project_id', projectId.toString());
    return this.get(this.config.getEndpoint('gen_route/in_project'), { params: params });
    // return EMPTY;
  }

  sendReport(projectId: number, ticked: any, address?: string): Observable<any> {
    const body = ReportConverter.toDbo(ticked);
    let params = this.createHttpParams()
      .append('project_id', projectId.toString());
    if (address) {
      params = params.append('address_attribute', address)
    }
    return this.post(this.config.getEndpoint('ticket/send'), body, { params });
  }

  saveReport(projectId: number, id: number, ticked: any, key: string): Observable<any> {
    const attributes = ReportConverter.toDboForUpdate(ticked);

    let attr = (key === 'transport' ? 'transportMode' : key);
    let params = this.createHttpParams()
      .append('attribute', attr)
      .append('entity_id', `${projectId}_${id}`)
      .append('entity_type', 'TICKET');

    let body = {
      'attributes': {
        [attr]: attributes[attr]
      }
    };
    if (!_.isEmpty(attributes[attr])) {
      return this.post(this.config.getEndpoint('entity/update_attribute'), body, { params });
    }
    return this.post(this.config.getEndpoint('entity/remove_values'), body, { params });
  }

  locationUpdate(projectId: number, id: number, ticked: any, key: string): Observable<any> {
    if (ticked.geometry.length === 0) return;

    let params = this.createHttpParams()
      .append('project_id', `${projectId}`)
      .append('ticket_id', `${id}`);

    let body = ticked.geometry[0];
    return this.post(this.config.getEndpoint('ticket/location_update'), body, { params });
  }

  statusUpdate(projectId: number, ticket_id: number, status: string, language: string): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', `${projectId}`)
      .append('ticket_id', `${ticket_id}`)
      .append('status', status)
      .append('language', language);

    return this.post(this.config.getEndpoint('ticket/update_status'), {}, { params });
  }

  getReport(projectId: number, tickedId: number): Observable<any> {
    let params = this.createHttpParams()
      .append('ids', `${projectId}_${tickedId}`);
    return this.get(this.config.getEndpoint('ticket/by_id'), { params });
  }

  getISentReport(projectId: number): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', `${projectId}`);
    return this.get(this.config.getEndpoint('ticket/i_sent'), { params });
  }

  searchReport(status: string, field: string, value: string): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const body = {
      project_id: project!.projectID!.id,
      reporter_filters: [],
      search_sub_projects: true,
      ticket_filters: [
        {
          "attribute": "closure",
          "value": status,
          "comparator": "EQUALS",
        },
        {
          "attribute": field,
          "value": value,
          "comparator": field !== 'date' ? "CONTAINS" : "EQUALS",
        }
      ],
    };
    return this
      .post(
        this.config.getEndpoint('ticket/search'),
        body,
        {})
      .pipe(
        map((response: Array<any>) => response.map(ReportConverter.toUiMReport))
      );
  }

  getAcceptedReport(projectId: number): Observable<any> {
    const body = {
      project_id: projectId,
      reporter_filters: [],
      search_sub_projects: true,
      ticket_filters: [
        {
          "attribute": "closure",
          "value": "ACCEPTED",
          "comparator": "EQUALS",
        }
      ],
    };
    return this.post(this.config.getEndpoint('ticket/search'), body, {});
  }

  getRejectedReport(projectId: number): Observable<any> {
    const body = {
      project_id: projectId,
      reporter_filters: [],
      search_sub_projects: true,
      ticket_filters: [
        {
          "attribute": "closure",
          "value": "REJECTED",
          "comparator": "EQUALS",
        }
      ],
    };
    return this.post(this.config.getEndpoint('ticket/search'), body, {});
  }

  getInboxReport(projectId: number): Observable<any> {
    const body = {
      project_id: projectId,
      reporter_filters: [],
      search_sub_projects: true,
      ticket_filters: [
        {
          "attribute": "closure",
          "value": "NEW",
          "comparator": "EQUALS",
        }
      ],
    };
    return this.post(this.config.getEndpoint('ticket/search'), body, {});
  }

  updateReportAttribute(projectId: number, id: number, ticked: any, key: string): Observable<any> {
    const attributes = ReportConverter.toDboForUpdate(ticked);

    let attr = (key === 'transport' ? 'transportMode' : key);
    let params = this.createHttpParams()
      .append('attribute', attr)
      .append('entity_id', `${projectId}_${id}`)
      .append('entity_type', 'TICKET');

    let body = {
      'attributes': {
        [attr]: attributes[attr]
      }
    };
    if (!_.isEmpty(attributes[attr])) {
      return this.post(this.config.getEndpoint('entity/update_attribute'), body, { params });
    }
    return this.post(this.config.getEndpoint('entity/remove_values'), body, { params });
  }

  addReportImage(projectId: number, id: number, image_id: number): Observable<any> {
    let params = this.createHttpParams()
      .append('attribute', 'image')
      .append('entity_id', `${projectId}_${id}`)
      .append('entity_type', 'TICKET');

    let body = {
      'attributes': {
        'image': [{
          id: image_id
        }]
      }
    };
    return this.post(this.config.getEndpoint('entity/add_values'), body, { params });
  }

  removeReportImage(projectId: number, id: number, image_id: number): Observable<any> {
    let params = this.createHttpParams()
      .append('attribute', 'image')
      .append('entity_id', `${projectId}_${id}`)
      .append('entity_type', 'TICKET');

    let body = {
      'attributes': {
        'image': [{
          id: image_id
        }]
      }
    };
    return this.post(this.config.getEndpoint('entity/remove_values'), body, { params });
  }

  getFreeProject(): Observable<any> {
    return this.get(this.config.getEndpoint('public/free_projects'));
  }

  getFreeProjectChecksum(projectPath: string): Observable<any> {

    if (this.checksum[projectPath]) return of(this.checksum[projectPath])

    return this.get(this.config.getEndpoint('public/request_project_id_checksum'), {
      params: {
        path_delimiter: '/',
        private_secret: 'bN2YrrBszR6EERrzKUFsRM0qAGTSE7tGuV4PnuOSGRXvxxBIMTmR5vsJnAgpdbHq',
        project_path: projectPath
      },
    }).pipe(
      tap(data => {
        this.checksum[projectPath] = data;
      })
    );
  }

  getFreeProjectData(pid: string, checksum: string): Observable<any> {
    return this.get(this.config.getEndpoint('public/project_data'),
      {
        params: {
          project_checksum: checksum,
          project_id: pid
        },
      });
  }

  getReports(projectId: number): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', projectId.toString());
    return this.get(this.config.getEndpoint('ticket/i_sent'), { params });
  }

  updateRouteVisibility(visibility: boolean, projectId: string, routeProjectId: string, routeId: string): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId)
      .append('route_project_id', routeProjectId)
      .append('route_id', routeId)
      .append('visible', JSON.stringify(visibility));
    return this.post<any>(this.config.getEndpoint('route/update_visibility'), null, { params });
  }

  saveRoute(projectId: number, route: Route): Observable<any> {

    const endpoints = {
      'generalized': {
        'add': this.config.getEndpoint('gen_route/add'),
        'update': this.config.getEndpoint('gen_route/update_segments')
      },
      'crowd': {
        'add': this.config.getEndpoint('route/send'),
        'update': this.config.getEndpoint('route/update_segments')
      }
    };

    let params = this.createHttpParams().set('project_id', projectId.toString());
    let data = route.data(null, projectId);

    if (route.id) {
      params = params.set(route.mapType == 'generalized' ? 'gen_route_id' : 'route_id', route.id.toString())
        .set('management_mode', 'true');
      return this.post<any>(endpoints[route.mapType]['update'], data, { params: params }).pipe(
        tap(result => route.id = result.entityID.id2),
        mergeMap(result => this.getRouteById(route.mapType, projectId, result.entityID.id2)),
        tap(result => result.segments.forEach((s, i) => route.segments[i].id = s.entityID.id2))
      );
    } else {
      let routeData = {
        'attributes': { 'mode': ['FOOT'], 'creation_mode': ['MANUAL_PLOTTING'] },
        'segments': data
      };
      return this.post<any>(endpoints[route.mapType]['add'], routeData, { params: params }).pipe(
        tap(result => route.id = result.id2),
        mergeMap(result => this.getRouteById(route.mapType, projectId, result.id2)),
        tap(result => result.segments.forEach((s, i) => route.segments[i].id = s.entityID.id2))
      );
    }
  }

  getRouteById(mapType: MapType, projectId: number, routeId: number): Observable<any> {
    let params = this.createHttpParams().append('ids', `${projectId}_${routeId}`);
    return this.get<any[]>(this.config.getEndpoint(mapType == 'generalized' ? 'gen_route/by_id' : 'route/by_id'), { params }).pipe(
      map(result => result[0])
    );
  }

  updateSegmentVisibility(mapType: MapType, projectId: number, routeId: number, segmentId: number, visible: boolean): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('visible', JSON.stringify(visible))
      .append('management_mode', 'true');

    let endpoint = this.config.getEndpoint(mapType == 'generalized' ? 'gen_route/update_segment_visibility' : 'route/update_segment_visibility');

    if (mapType == 'generalized') {
      params = params
        .append('gen_route_project_id', projectId.toString())
        .append('gen_route_id', routeId.toString())
        .append('gen_segment_id', segmentId.toString());
    } else {
      params = params
        .append('route_project_id', projectId.toString())
        .append('route_id', routeId.toString())
        .append('segment_id', segmentId.toString());
    }

    return this.post<any>(endpoint, null, { params });
  }

  updateSegmentAccessibility(projectId: number, routeId: number, segmentId: number, accessibility: Accessibility) {
    let params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('route_id', routeId.toString())
      .append('segment_id', segmentId.toString())
      .append('accessibility', accessibility)
      .append('management_mode', 'true');

    return this.post<any>(this.config.getEndpoint('route/update_segment_accessibility'), null, { params });
  }

  updateSegmentDescription(projectId: number, routeId: number, segmentId: number, description: string) {
    let params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('route_id', routeId.toString())
      .append('segment_id', segmentId.toString())
      .append('accessibility_description', description)
      .append('management_mode', 'true');

    return this.post<any>(this.config.getEndpoint('route/update_segment_accessibility_description'), null, { params });
  }


  routeSend(projectId: number, route: Route, transport: string, companions: string[]): Observable<any> {
    let params = this.createHttpParams().set('project_id', projectId.toString());
    let data;

    data = {
      'attributes': { 'mode': [transport], 'creation_mode': ['MANUAL_TRACKING'], 'child_travel_companions': companions },
      'segments': route.data(null, projectId)
    };

    return this.post<any>(this.config.getEndpoint('route/send'), data, { params: params });
  }

  getISentRoute(): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    let params = this.createHttpParams()
      .append('project_id', project!.projectID!.id);
    return this.get(this.config.getEndpoint('route/i_sent'), { params });
  }


  genRouteCreate(projectId: number) {
    let data = {
      'project_id': projectId
    };

    return this.post(this.config.getEndpoint('gen_route/create'), data);
  }

  getProjectData(projectId: string): Observable<any> {
    const params = this.createHttpParams().append('project_id', projectId);
    return this.get(this.config.getEndpoint('member/project_data'), { params });
  }

  getPublicProjectData(projectId: string, projectChecksum: string): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('project_checksum', projectChecksum);
    return this.get(this.config.getEndpoint('public/project_data'), { params });
  }

  getURLoverviewMap(projectId: string): Observable<any> {
    const params = this.createHttpParams().append('project_id', projectId);
    return this.get(this.config.getEndpoint('member/project_overview_map_url'), { params });
  }

  deleteProject(projectId: string): Observable<any> {
    const params = this.createHttpParams().append('project_id', projectId);
    return this.delete(this.config.getEndpoint('member/project_remove_recursively'), { params });
  }

  getProjectLeaderEmail(projectId: string): Observable<any> {
    const params = this.createHttpParams().append('project_id', projectId);
    return this.get(this.config.getEndpoint('member/project_manager_first'), { params });
  }

  createSubProject(newProject): Observable<any> {
    const body = {
      language_email: newProject.lng,
      location: newProject.location,
      new_project_manager: newProject.emailLeader,
      parent_project: newProject.parentId as number,
      project_address: newProject.address,
      project_name: newProject.name
    };
    return this.post(this.config.getEndpoint('member/project_create_sub_project'), body, {});
  }

  updateProject(project: any, geocodeAddress: boolean): Observable<any> {

    const params = new HttpParams()
      .append('geocode_address', String(geocodeAddress));

    const uProject = _.cloneDeep(project);
    if (project.street && project.street.name) {
      uProject.street = project.street.name;
    }

    return this.post(this.config.getEndpoint('member/project_data_update'), uProject, { params });
  }

  updateProjectManager(project): Observable<any> {

    const params = this.createHttpParams()
      .append('project_id', project!.projectID!.id)
      .append('new_project_manager', project.email);

    return this.post(this.config.getEndpoint('member/project_manager_update'), {}, { params });
  }

  getUsers(): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('project_id', project!.projectID!.id);
    return this.get(this.config.getEndpoint('member/project_memberships_light'), { params });
  }

  enableUser(user): Observable<any> {
    const enable = JSON.stringify(!user.status);
    const params = this.createHttpParams()
      .append('project_id', user.projectId)
      .append('user_ids', user.email)
      .append('enable', enable);
    return this.post(this.config.getEndpoint('member/users_enable'), null, { params });
  }

  addUser(email): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('email_addresses', email)
      .append('project_id', project!.projectID!.id);
    return this.post(this.config.getEndpoint('member/users_add'), null, { params });
  }

  deleteUser(email): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('user_ids', email)
      .append('project_id', project!.projectID!.id);
    return this.delete(this.config.getEndpoint('member/users_remove'), { params });
  }

  inviteUser(email): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const language = localStorage.getItem('project_lng');
    const body = {
      language_email: language || 'ENG',
      message: 'I want invite you',
      project_id: project!.projectID!.id,
      user_id: email
    };
    return this.post(this.config.getEndpoint('member/mail_user_invitation'), body, {});
  }

  uploadXslUsers(formData: FormData): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('project_id', project!.projectID!.id);
    return this.put(this.config.getEndpoint('member/users_add_from_spreadsheet'), formData, { params });
  }

  activateUserMail(user, lang: string): Observable<any> {
    const enable = JSON.stringify(!user.status);
    console.log(enable);
    const suffix = enable == 'true' ? 'enabled' : 'disabled';
    const endpoint = `member/mail_user_${suffix}`;
    console.log(enable, suffix, endpoint);

    const language = localStorage.getItem('project_lng');
    const body = {
      language_email: language || 'ENG',
      message: `Your account permission is ${enable ? 'allowed' : 'revoked'}`,
      project_id: user.projectId,
      user_id: user.email
    };
    return this.post(this.config.getEndpoint(endpoint), body, {});
  }

  getDictionary(lang): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams().append('project_id', project!.projectID!.id).append('language', lang);
    return this.get(`/api2/member/project_dictionary`, { params }).pipe(map(res => res['map']));
  }

  addUserWithPwd(email, password, passwordCheck): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('email_address', email)
      .append('password', password)
      .append('password_check', passwordCheck)
      .append('project_id', project!.projectID!.id);
    return this.post(this.config.getEndpoint('member/user_add_with_password'), null, { params });
  }

  getLayerSets(projectId: number): Observable<any[]> {
    const params = this.createHttpParams().append('project_id', projectId.toString());
    return this.get<any[]>(this.config.getEndpoint('layer/layer_sets'), { params });
  }

  getLayers(projectId: number): Observable<any[]> {
    const params = this.createHttpParams().append('project_id', projectId.toString());
    return this.get<any[]>(this.config.getEndpoint('layer/entity_layers_in_project'), { params });
  }


  getProjectChecksum(projectId: number): Observable<any> {
    const params = this.createHttpParams().append('projectID', projectId.toString());
    return this.get(this.config.getEndpoint('layer/project_checksum'), { params });
  }

  getPublicLayerSets(projectId: number, projectChecksum): Observable<any[]> {
    const params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('project_checksum', projectChecksum);
    return this.get<any[]>(this.config.getEndpoint('public/layer_sets'), { params });
  }

  getPublicLayers(projectId: number, projectChecksum): Observable<any[]> {
    const params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('project_checksum', projectChecksum);
    return this.get<any[]>(this.config.getEndpoint('public/layers'), { params });
  }

  getPubDictionary(language: string, project_checksum: string, project_id: number): Observable<any> {
    const params = this.createHttpParams()
      .set('project_id', project_id.toString())
      .set('language', language)
      .set('project_checksum', project_checksum);

    return this.get(this.config.getEndpoint('public/dictionary'), { params });
  }

  getImage(imageId: string): Observable<any> {
    const params = this.createHttpParams().append('image_id', imageId);
    return this.get(this.config.getEndpoint('public/image'), { responseType: 'json', params }).pipe(
      catchError(e => of({
        'byteArray': 'error',
        'description': 'resource loaded from byte array',
        'readable': true,
        'open': false
      }))
    );
  }

  getEntities(projectId: number, layerId: number, project_id_or_scheme_name, from_root_project: boolean): Observable<any[]> {
    const params = this.createHttpParams()
      .set('project_id', projectId.toString())
      .set('layer_id', layerId.toString())
      .set('from_root_project', JSON.stringify(from_root_project))
      .set('project_id_or_scheme_name', project_id_or_scheme_name);

    return this.get<any[]>(this.config.getEndpoint('layer/entities'), { params });
  }

  getPublicEntities(projectId: number, layerId: number, projectChecksum, layerChecksum): Observable<any[]> {
    const params = this.createHttpParams()
      .set('project_id', projectId.toString())
      .set('layer_id', layerId.toString())
      .set('layer_checksum', layerChecksum)
      .set('project_checksum', projectChecksum)
      .set('project_id_or_scheme_name', 'SCHOOLS');

    return this.get<any[]>(this.config.getEndpoint('public/entities_in_layer'), { params });
  }

  isPasswordUpdateAvailable(): Observable<any> {
    const params = this.createHttpParams();
    return this.get(this.config.getEndpoint('auth/password/update'), {});
  }


  updatePwd(currentPassword, newPassword): Observable<any> {
    const body = {
      "oldPassword": currentPassword,
      "newPassword": newPassword
    };
    return this.post(this.config.getEndpoint('auth/password/update'), body);
  }

  getPublicProjectIcons(projectId, projectChecksum): Observable<any> {

    const params = this.createHttpParams()
      .append('project_id', projectId.toString())
      .append('project_checksum', projectChecksum);
    return this.get(this.config.getEndpoint('public/project_icons'), { params });
  }

  getProjectIcon1(projectId): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId);
    return this.get(this.config.getEndpoint('member/project_icon1'), { params });
  }

  getProjectIcon2(projectId): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId);
    return this.get(this.config.getEndpoint('member/project_icon2'), { params });
  }

  updateProjectIcon1(imageId, projectId): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', projectId);
    if (imageId) {
      params = params.append('image_id', imageId);
    }
    return this.post(this.config.getEndpoint('member/project_icon1_update'), null, { params });
  }

  updateProjectIcon2(imageId, projectId): Observable<any> {
    let params = this.createHttpParams()
      .append('project_id', projectId);
    if (imageId) {
      params = params.append('image_id', imageId);
    }
    return this.post(this.config.getEndpoint('member/project_icon2_update'), null, { params });
  }

  likeTicket(projectId, ticketId): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId)
      .append('ticket_id', ticketId);
    return this.post(this.config.getEndpoint('ticket/like'), null, { params });
  }

  isLikedTicket(projectId, ticketId): Observable<boolean> {
    const params = this.createHttpParams()
      .append('project_id', projectId)
      .append('ticket_id', ticketId);
    return this.get<boolean>(this.config.getEndpoint('ticket/liked'), { params });
  }

  addMessageToTicket(projectId, ticketId, msg): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId)
      .append('email_to_reporters', 'false')
      .append('email_to_requestor', 'false')
      .append('language', 'ENG')
      .append('message', msg)
      .append('ticket_id', ticketId);
    return this.post(this.config.getEndpoint('ticket/add_message'), null, { params });
  }

  getMineRoutes(): Observable<any[]> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('project_id', project!.projectID!.id);
    return this.get<any[]>(this.config.getEndpoint('route/mine'), { params });
  }

  getAllRoutes(): Observable<any[]> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('project_id', project!.projectID!.id);
    return this.get<any[]>(this.config.getEndpoint('route/all'), { params });
  }

  updateRouteMode(id, mode, project_id = null): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('route_id', id)
      .append('mode', mode)
      .append('project_id', project_id || project!.projectID!.id)
      .append('management_mode', 'true');
    return this.post<any>(this.config.getEndpoint('route/update_mode'), null, { params });
  }

  updateRouteCompanions(id, companions, project_id = null): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));

    let params = this.createHttpParams()
      .append('route_id', id)
      .append('project_id', project_id || project!.projectID!.id)
      .append('management_mode', 'true');

    if (companions.length > 0) {
      params = this.createHttpParams()
      .append('route_id', id)
      .append('project_id', project_id || project!.projectID!.id)
      .append('management_mode', 'true')
      .append('child_travel_companions', companions.join(','));
    }
    return this.post<any>(this.config.getEndpoint('route/update_child_travel_companions'), null, { params });
  }

  removeRoute(routeId): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('route_id', routeId)
      .append('project_id', project!.projectID!.id);
    return this.delete<any>(this.config.getEndpoint('route/remove'), { params });
  }

  getRoute(id): Observable<any> {
    const params = this.createHttpParams()
      .append('ids', id);
    return this.get(this.config.getEndpoint('route/by_id'), { params });
  }

  setAdvice(advice: string, projectId: string, reportId): Observable<any> {
    const params = this.createHttpParams()
      .append('ticket_id', reportId)
      .append('advice', advice)
      .append('project_id', projectId);
    return this.post<any>(this.config.getEndpoint('ticket/set_advice'), null, { params });
  }

  updateTicketVisibility(visibility: boolean, projectId: string, reportId: string): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id', projectId)
      .append('ticket_id', reportId)
      .append('visible', JSON.stringify(visibility));
    return this.post<any>(this.config.getEndpoint('ticket/update_visibility'), null, { params });
  }

  getReporters(projectId: string, reportId): Observable<any> {
    const params = this.createHttpParams()
      .append('ticket_id', reportId)
      .append('project_id', projectId);
    return this.get<any>(this.config.getEndpoint('ticket/reporters'), { params });
  }

  getReportHistory(projectId: string, reportId): Observable<any> {
    const params = this.createHttpParams()
      .append('ticket_id', reportId)
      .append('project_id', projectId);
    return this.get<any>(this.config.getEndpoint('ticket/history'), { params });
  }

  restoreReport(projectId: string, reportId): Observable<any> {
    const params = this.createHttpParams()
      .append('ticket_id', reportId)
      .append('project_id', projectId);
    return this.post<any>(this.config.getEndpoint('ticket/restore_rejected'), null, { params });
  }

  getPublicEntitiesInLayer(layerChecksum: string, layerId: string, path, projectId, projectIdOrScheme): Observable<any> {

    return this.getFreeProjectChecksum(path).pipe(
      mergeMap(data => {
        const params = this.createHttpParams()
          .append('layer_checksum', layerChecksum)
          .append('layer_id', layerId)
          .append('project_checksum', data.checksum)
          .append('project_id', projectId)
          .append('project_id_or_scheme_name', projectIdOrScheme);
        return this.get<any>(this.config.getEndpoint('public/entities_in_layer'), { params });
      })
    );

  }

  createNewLayer(layerData: any): Observable<any> {
    const body = LayerConverter.toDbo(layerData);

    return this.post(this.config.getEndpoint('layer/create_layer_entities'), body, {});
  }

  updateLayerEntity(layerData: any): Observable<any> {
    const body = LayerConverter.toDboForUpdate(layerData);

    return this.post(this.config.getEndpoint('layer/update_entity_layer'), body, {});
  }

  removeLayer(projectId: number, layerId: number): Observable<any> {

    const params = this.createHttpParams()
      .append('project_id_layer', projectId.toString())
      .append('layer_id', layerId.toString());

    return this.delete(this.config.getEndpoint('layer/remove'), { params });
  }

  createEntity(layerData, entityData: any, projectID): Observable<any> {

    const params = this.createHttpParams()
      .append('project_id_layer', layerData.projectId)
      .append('layer_id', layerData.layerId)
      .append('project_id_common_entity', projectID);

    const body = EntityConverter.toDbo(entityData);

    return this.post(this.config.getEndpoint('layer/create_common'), body, { params });
  }

  updateEntity(entityData: any) {

  }

  uploadShapeFile(shapefile, layerData, projectID): Observable<any> {

    var formData: any = new FormData();
    formData.append("shapeData", shapefile);
    formData.append("zipped_shape_file", shapefile);

    const params = this.createHttpParams()
      .append('project_id_or_scheme_name', layerData.projectId)
      .append('layer_id', layerData.layerId)
      .append('project_id', projectID);

    return this.put(this.config.getEndpoint('layer/add_from_shape'), formData, {
      params,
    });
  }

  removeEntity(projectIdSchemeName: string, layerId: string, projectId: string, entityId: string): Observable<any> {
    const params = this.createHttpParams()
      .append('project_id_or_scheme_name', projectIdSchemeName.toString())
      .append('layer_id', layerId.toString())
      .append('project_id', projectId.toString())
      .append('entity_id', entityId.toString());
    return this.delete(this.config.getEndpoint('layer/remove_common'), { params });
  }

  getTicketReport(file_format: string, language: string): any {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('file_format', file_format)
      .append('language', language)
      .append('project_id', project!.projectID!.id);

    return this.get(this.config.getEndpoint('ticket/spreadsheet'), {
      params: params,
      responseType: 'blob'
    });
  }

  generateReport(language: string): any {
    const project = JSON.parse(localStorage.getItem('project_id'));
    const params = this.createHttpParams()
      .append('ppt_language', 'NLD' || language)
      .append('project_id', project!.projectID!.id);

    return this.get(this.config.getEndpoint('report/generate'), {
      params: params,
      responseType: 'blob'
    });
  }

  getMyinfo(): Observable<boolean> {
    let params = this.createHttpParams().set('app_scheme', 'SCHOOLS');

    return this.get<boolean>(this.config.getEndpoint('/member/my_info'), { params: params });
  }

  private createHttpParams(): HttpParams {
    return new HttpParams({ encoder: new CustomHttpParamsEncoder() });
  }
}
